"use client";

import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { type CellContext, type ColumnDef, type Row, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { type VirtualItem, useWindowVirtualizer } from "@tanstack/react-virtual";
import { usePathname, useSearchParams } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "@/components/loader";
import { Price } from "@defuse/ui/price";
import { QueryFailed } from "@/components/query-failed";
import { MaskingBadge } from "@/components/masking-badge";
import { RecipientLine } from "@/components/recipient";
import { LinkWithLoader } from "@/components/route-loader";
import { Senders } from "@/components/senders";
import { Status } from "@/components/status";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@defuse/ui/table";
import { PREV_PATH_STORAGE_KEY } from "@/hooks/usePrevPath";
import { usePrevious } from "@/hooks/usePrevious";
import { useBreakpoint } from "@/hooks/useScreenSize";
import { useScrollRestoration } from "@/hooks/useScrollRestoration";
import { useTabActivity } from "@/hooks/useTabActivity";
import { shouldHideAmountsForStatus, statusesToTrack } from "@intents-explorer/shared-api/status";
import type { TransactionsResponse, TransactionItem, GetTransactionsParsedSearchParams } from "@intents-explorer/shared-api/transactions/transactionsApiV1";
import { getTransactions } from "@/lib/transactions/api/getTransactions";
import { getMaskingTransactionsByIds } from "@/lib/transactions/api/getMaskingTransactionsByIds";
import { getTransactionsByIds } from "@/lib/transactions/api/getTransactionsByIds";
import { TRANSACTIONS_POLLING_INTERVAL_MS, TRANSACTIONS_BY_IDS_POLLING_INTERVAL_MS } from "@/lib/transactions/polling-intervals";
import { MAX_NUMBER_OF_TRANSACTIONS, buildTransactionPageHref, getTransactionCacheKey } from "@intents-explorer/shared-api/transactions/transactionsCommon";
import { numToStr, readonlyURLSearchParamsToStr, URLSearchParamsToStr } from "@/lib/type-safe-stringify";
import { removeProperties } from "@intents-explorer/shared-api/utils";
import { logger } from "@intents-explorer/shared-api/logger";
import { FormattedTime } from "@defuse/ui/formatted-time";
import { Referral } from "./referral";
import { useScrollToTop } from "./scroll-to-top-provider";
type DepositKey = {
  depositAddress: string;
  depositMemo: string | null;
};
type LinkCell = Pick<TransactionItem, "source" | "status"> & DepositKey;
type FromCell = Pick<TransactionItem, "amountInFormatted" | "originAsset" | "depositType"> & {
  senders: string[];
  amountInUsd?: string | undefined;
  isPriceHidden: boolean;
};
type ToCell = Pick<TransactionItem, "amountOutFormatted" | "destinationAsset" | "recipientType"> & {
  recipient?: string | undefined;
  amountOutUsd?: string | undefined;
  isPriceHidden: boolean;
};
type StatusCell = Pick<TransactionItem, "status">;
type CreatedAtCell = Pick<TransactionItem, "createdAt">;
type ReferralCell = {
  referral: string | null;
  affiliates: string[] | undefined;
};
const HoveredRowContext = createContext<boolean>(false);
function FromComponent(t0) {
  const $ = _c(24);
  const {
    q,
    pathname,
    isLargeScreen,
    scrollToTop
  } = t0;
  let t1;
  if ($[0] !== q) {
    t1 = q.getValue();
    $[0] = q;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    amountInFormatted,
    originAsset,
    senders,
    depositType,
    amountInUsd,
    isPriceHidden
  } = t1;
  const isHovered = useContext(HoveredRowContext);
  let t2;
  if ($[2] !== pathname) {
    t2 = sender => {
      const newParams = new URLSearchParams();
      newParams.set("search", sender);
      const stringURLSearchParams = URLSearchParamsToStr(newParams);
      return stringURLSearchParams !== "" ? `${pathname}?${stringURLSearchParams}` : pathname;
    };
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getLink = t2;
  const t3 = isLargeScreen ? "280px" : "200px";
  let t4;
  if ($[4] !== t3) {
    t4 = {
      width: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== amountInFormatted || $[7] !== amountInUsd || $[8] !== depositType || $[9] !== isHovered || $[10] !== isPriceHidden || $[11] !== originAsset) {
    t5 = amountInFormatted !== undefined && originAsset !== undefined ? <Price type={depositType} price={amountInFormatted} defuseAssetId={originAsset} usdAmount={amountInUsd} showUsdAmount={isHovered} isPriceHidden={isPriceHidden} /> : <MaskingBadge />;
    $[6] = amountInFormatted;
    $[7] = amountInUsd;
    $[8] = depositType;
    $[9] = isHovered;
    $[10] = isPriceHidden;
    $[11] = originAsset;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== depositType || $[14] !== getLink || $[15] !== scrollToTop || $[16] !== senders) {
    t6 = senders.length > 0 ? <Senders senders={senders} hashLength={9} getLink={getLink} onInternalLinkClick={scrollToTop} scroll={false} compact={true} className="min-h-[20px]" /> : depositType === "CONFIDENTIAL_INTENTS" ? <><span>Sender:</span><span>Confidential</span></> : null;
    $[13] = depositType;
    $[14] = getLink;
    $[15] = scrollToTop;
    $[16] = senders;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = <div className="text-muted-foreground flex min-h-[20px] gap-1 text-sm">{t6}</div>;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t5 || $[22] !== t7) {
    t8 = <div className="flex flex-col gap-3 px-4" style={t4}>{t5}{t7}</div>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function ToComponent(t0) {
  const $ = _c(22);
  const {
    q,
    pathname,
    isLargeScreen,
    scrollToTop
  } = t0;
  let t1;
  if ($[0] !== q) {
    t1 = q.getValue();
    $[0] = q;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    amountOutFormatted,
    destinationAsset,
    recipient,
    recipientType,
    amountOutUsd,
    isPriceHidden
  } = t1;
  const isHovered = useContext(HoveredRowContext);
  let t2;
  if ($[2] !== pathname) {
    t2 = recipient_0 => {
      const newParams = new URLSearchParams();
      newParams.set("search", recipient_0);
      return `${pathname}?${URLSearchParamsToStr(newParams)}`;
    };
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getRecipientLink = t2;
  const t3 = isLargeScreen ? "280px" : "200px";
  let t4;
  if ($[4] !== t3) {
    t4 = {
      width: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== amountOutFormatted || $[7] !== amountOutUsd || $[8] !== destinationAsset || $[9] !== isHovered || $[10] !== isPriceHidden || $[11] !== recipientType) {
    t5 = amountOutFormatted !== undefined && destinationAsset !== undefined ? <Price type={recipientType} price={amountOutFormatted} defuseAssetId={destinationAsset} usdAmount={amountOutUsd} showUsdAmount={isHovered} isPriceHidden={isPriceHidden} /> : <MaskingBadge />;
    $[6] = amountOutFormatted;
    $[7] = amountOutUsd;
    $[8] = destinationAsset;
    $[9] = isHovered;
    $[10] = isPriceHidden;
    $[11] = recipientType;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== getRecipientLink || $[14] !== recipient || $[15] !== recipientType || $[16] !== scrollToTop) {
    t6 = <RecipientLine className="text-muted-foreground flex min-h-[20px] gap-1 text-sm" recipientType={recipientType} recipient={recipient} length={9} getLink={getRecipientLink} onInternalLinkClick={scrollToTop} recipientDisplayMode="table" scroll={false} />;
    $[13] = getRecipientLink;
    $[14] = recipient;
    $[15] = recipientType;
    $[16] = scrollToTop;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <div className="flex flex-col gap-3 px-4" style={t4}>{t5}{t6}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
function getColumns({
  isLargeScreen,
  pathname,
  scrollToTop
}: {
  isLargeScreen: boolean;
  pathname: string;
  scrollToTop?: (() => void) | undefined;
}): ColumnDef<TransactionItem>[] {
  return [{
    id: "link",
    accessorFn: (tx): LinkCell => {
      return {
        depositAddress: tx.depositAddress,
        depositMemo: tx.depositMemo,
        source: tx.source,
        status: tx.status
      };
    },
    header: () => {
      return <div className="sr-only">Link</div>;
    },
    cell: q => {
      const {
        depositAddress,
        depositMemo,
        source
      } =
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- tanstack table getValue() returns unknown
      q.getValue() as LinkCell;
      const href = source === "masking" ? `/mask/${depositAddress}${depositMemo !== null ? `?depositMemo=${encodeURIComponent(depositMemo)}` : ""}` : buildTransactionPageHref({
        depositAddress,
        depositMemo
      });
      return <LinkWithLoader href={href} className="absolute inset-0" onClick={() => {
        window.sessionStorage.setItem(PREV_PATH_STORAGE_KEY, window.location.href);
      }}>
						<div className="sr-only">Details</div>
					</LinkWithLoader>;
    },
    size: 0
  }, {
    accessorFn: (tx): FromCell => {
      const senders = (tx.quoteTransactions ?? []).flatMap(qt => {
        return qt.sender !== undefined ? [qt.sender] : [];
      });
      return {
        amountInFormatted: tx.amountInFormatted,
        originAsset: tx.originAsset,
        depositType: tx.depositType,
        amountInUsd: tx.amountInUsd ?? undefined,
        senders,
        isPriceHidden: tx.source === "swaps" && shouldHideAmountsForStatus(tx.status, tx.hasNearSwapTransaction ?? false)
      };
    },
    header: "From",
    cell: q => {
      return <FromComponent
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      q={q as CellContext<TransactionItem, FromCell>} pathname={pathname} isLargeScreen={isLargeScreen} scrollToTop={scrollToTop} />;
    },
    size: isLargeScreen ? 280 : 200
  }, {
    accessorFn: (tx): ToCell => {
      return {
        amountOutFormatted: tx.amountOutFormatted,
        destinationAsset: tx.destinationAsset,
        recipient: tx.recipient,
        recipientType: tx.recipientType,
        amountOutUsd: tx.amountOutUsd ?? undefined,
        isPriceHidden: tx.source === "swaps" && shouldHideAmountsForStatus(tx.status, tx.hasNearSwapTransaction ?? false)
      };
    },
    header: "To",
    cell: q => {
      return <ToComponent
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      q={q as CellContext<TransactionItem, ToCell>} pathname={pathname} isLargeScreen={isLargeScreen} scrollToTop={scrollToTop} />;
    },
    size: isLargeScreen ? 280 : 200
  }, {
    id: "status",
    header: "Status",
    accessorFn: ({
      status
    }): StatusCell => {
      return {
        status
      };
    },
    cell: q => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const {
        status
      } = q.getValue() as StatusCell;
      return <div className="px-4" style={{
        width: "175px"
      }}>
						<Status status={status} className="w-full" />
					</div>;
    },
    size: 175
  }, {
    id: "createdAt",
    header: () => {
      return <div className="w-full text-right">Created At</div>;
    },
    accessorFn: ({
      createdAt
    }): CreatedAtCell => {
      return {
        createdAt
      };
    },
    cell: q => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const {
        createdAt
      } = q.getValue() as CreatedAtCell;
      return <div className="px-4 text-right" style={{
        width: isLargeScreen ? "289px" : "193px"
      }}>
						<FormattedTime date={createdAt} showRelativeTime={true} />
					</div>;
    },
    size: isLargeScreen ? 289 : 193
  }, {
    id: "referral",
    accessorFn: (tx): ReferralCell => {
      return {
        referral: tx.referral,
        affiliates: tx.affiliates
      };
    },
    header: () => {
      return <div className="sr-only">Referral</div>;
    },
    cell: q => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const {
        referral,
        affiliates
      } = q.getValue() as ReferralCell;
      return <div className="relative h-full">
						<Referral referral={referral} affiliates={affiliates} className="absolute top-1.5 right-3" />
					</div>;
    },
    size: 0
  }];
}
function TableRowWithHover({
  row,
  virtualRow,
  flashedRows,
  addFlashedRow,
  timeoutsForFlashingRows
}: {
  row: Row<TransactionItem>;
  virtualRow: VirtualItem;
  flashedRows: ReturnType<typeof useFlashedRows>["flashedRows"];
  addFlashedRow: (id: string) => void;
  timeoutsForFlashingRows: ReturnType<typeof useRef<Record<string, NodeJS.Timeout>>>;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unnecessary-type-assertion
  const tx = row.getValue("link") as LinkCell;
  const txId = getTransactionCacheKey(tx);
  const flashedRowsCurrent = flashedRows.current;
  const isNew = flashedRowsCurrent === null || !flashedRowsCurrent.has(txId);
  if (isNew && timeoutsForFlashingRows.current !== undefined && timeoutsForFlashingRows.current[txId] === undefined) {
    addFlashedRow(txId);

    // eslint-disable-next-line react-hooks/immutability
    timeoutsForFlashingRows.current[txId] = setTimeout(() => {
      if (timeoutsForFlashingRows.current !== undefined) {
        removeProperties(timeoutsForFlashingRows.current, ([key]) => {
          return key === txId;
        });
      }
    }, 2000);
  }
  const showAnimation = timeoutsForFlashingRows.current?.[txId] !== undefined;
  const isActive = isHovered || isFocused;
  return <HoveredRowContext.Provider value={isActive}>
			<TableRow key={row.id} className={`hover:from-brand-orange/5 hover:via-brand-yellow/5 hover:to-brand-orange/5 absolute flex h-[93px] w-full max-w-3xl rounded-lg border-b-0 hover:bg-linear-to-r hover:backdrop-blur-sm xl:max-w-5xl ${showAnimation ? "animate-[flash_1s_ease-in-out]" : ""}`} style={{
      transform: `translateY(${numToStr(virtualRow.start)}px)`,
      ...(showAnimation ? {
        animationFillMode: "forwards"
      } : {})
    }} onMouseEnter={() => {
      setIsHovered(true);
    }} onMouseLeave={() => {
      setIsHovered(false);
    }} onTouchStart={() => {
      setIsHovered(true);
    }} onTouchEnd={() => {
      setIsHovered(false);
    }} onFocusCapture={() => {
      setIsFocused(true);
    }} onBlurCapture={() => {
      setIsFocused(false);
    }}>
				{row.getVisibleCells().map(cell => {
        return <TableCell key={cell.id} className="flex items-center p-0" style={{
          width: cell.column.getSize()
        }}>
							<div className="flex h-full w-full items-center">
								{flexRender(cell.column.columnDef.cell, cell.getContext())}
							</div>
						</TableCell>;
      })}
			</TableRow>
		</HoveredRowContext.Provider>;
}

/**
 * Carries what the panel needs: the id to show and which end of the list failed.
 * A subclass rather than `cause` so reading it back stays type-safe.
 */
class TransactionsFetchError extends Error {
  constructor(code: string, readonly correlationId: string | null, readonly fetched: "newer" | "older") {
    super(code);
    this.name = "TransactionsFetchError";
  }
}
type Failure = {
  correlationId: string | null;
  fetched: "newer" | "older" | "initial";
};

/**
 * Retries are off and both scroll triggers stop once a fetch fails, so at most
 * one failure exists at a time and its direction alone places the panel.
 */
function resolveFailure(error: Error | null, initialFailure: string | undefined): Failure | undefined {
  if (error instanceof TransactionsFetchError) {
    return {
      correlationId: error.correlationId,
      fetched: error.fetched
    };
  }
  if (error !== null) {
    return {
      correlationId: null,
      fetched: "older"
    };
  }
  return initialFailure === undefined ? undefined : {
    correlationId: initialFailure,
    fetched: "initial"
  };
}
interface TransactionsTableProps {
  params: GetTransactionsParsedSearchParams;
  initialTransactions: TransactionsResponse;
  initialFailure: string | undefined;
}
export function TransactionsTable({
  initialTransactions,
  initialFailure,
  params
}: TransactionsTableProps) {
  const {
    flashedRows,
    addFlashedRow,
    clearFlashedRows
  } = useFlashedRows(initialTransactions.items);
  const timeoutsForFlashingRows = useRef<Record<string, NodeJS.Timeout>>({});
  const queryClient = useQueryClient();
  const [visibleAddresses, setVisibleAddresses] = useState<{
    swaps: DepositKey[];
    masking: DepositKey[];
  }>({
    swaps: [],
    masking: []
  });
  const pathname = usePathname();
  const isLargeScreen = useBreakpoint("xl");
  const {
    scrollToTop,
    scrollToTopRef
  } = useScrollToTop();
  const restoreScrollPosition = useScrollRestoration();
  const columns = useMemo(() => {
    return getColumns({
      isLargeScreen,
      pathname,
      scrollToTop
    });
  }, [isLargeScreen, pathname, scrollToTop]);
  const [hasReachedTheEnd, setHasReachedTheEnd] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const lastFetchPreviousPageTime = useRef<number>(Date.now());
  type PageParam = {
    prevCursor: string;
  } | {
    nextCursor: string;
  } | undefined;
  const {
    data,
    error,
    fetchNextPage,
    fetchPreviousPage,
    isFetching
  } = useInfiniteQuery<TransactionsResponse, Error, InfiniteData<TransactionsResponse>, unknown[], PageParam>({
    queryKey: ["quotes", params],
    // Failures require a reload or filter change, without background retries.
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: async ({
      pageParam: pagination
    }) => {
      const isFetchingNewer = pagination !== undefined && "nextCursor" in pagination;
      const result = await getTransactions({
        ...params,
        ...pagination
      });
      if ("err" in result) {
        logger.error("[table] getTransactions failed", result.err);
        const {
          code,
          error: cause
        } = result.err.error;

        // Preserve loaded pages and cursors instead of appending an empty page.
        throw new TransactionsFetchError(code, typeof cause === "string" ? cause : null, isFetchingNewer ? "newer" : "older");
      }
      const response = result.ok;
      if (!isFetchingNewer) {
        for (const tx of response.items) {
          addFlashedRow(getTransactionCacheKey(tx));
        }
      }
      if (!isFetchingNewer && response.items.length === 0) {
        setHasReachedTheEnd(true);
      }
      return response;
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => {
      // getNextPageParam = fetch older = use prevCursor from response
      if (lastPage.prevCursor === undefined) {
        return undefined;
      }
      return {
        prevCursor: lastPage.prevCursor
      };
    },
    getPreviousPageParam: firstPage => {
      // getPreviousPageParam = fetch newer = use nextCursor from response
      if (firstPage.nextCursor === undefined) {
        return undefined;
      }
      return {
        nextCursor: firstPage.nextCursor
      };
    },
    // Use cached data if available, otherwise use SSR data
    initialData: () => {
      const existingData = queryClient.getQueryData<InfiniteData<TransactionsResponse, PageParam>>(["quotes", params]);
      return existingData ?? {
        pages: [initialTransactions],
        pageParams: [undefined]
      };
    }
  });
  const failure = resolveFailure(error, initialFailure);
  // Kept as a boolean so the fetch callbacks below get a stable dependency.
  const hasFailure = failure !== undefined;

  // flatten the array of arrays from the useInfiniteQuery hook
  const flatData = useMemo(() => {
    return data.pages.flatMap(page => {
      return page.items;
    });
  }, [data]);
  const [isNoResultsVisible, setIsNoResultsVisible] = useState(false);
  const [hasParamsChanged, setHasParamsChanged] = useState(false);
  const previousParams = usePrevious(JSON.stringify(params));
  useEffect(() => {
    const paramsString = JSON.stringify(params);
    if (previousParams !== null && previousParams !== paramsString) {
      setHasParamsChanged(true);
    }
  }, [params, previousParams]);
  useEffect(() => {
    if (hasParamsChanged && isFetching) {
      setIsNoResultsVisible(false);
      setHasParamsChanged(false);
    } else if (!isFetching && flatData.length === 0) {
      setIsNoResultsVisible(true);
    } else if (flatData.length > 0) {
      setIsNoResultsVisible(false);
    }
  }, [isFetching, flatData, hasParamsChanged]);
  const isTabActive = useTabActivity();

  // called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = useCallback(() => {
    if (isTabActive && !hasReachedTheEnd && !hasFailure) {
      const {
        scrollHeight,
        scrollTop,
        clientHeight
      } = document.documentElement;

      // once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
      if (scrollHeight - scrollTop - clientHeight < 500 && !isFetching && flatData.length > 0) {
        setIsFetchingNextPage(true);
        void fetchNextPage({
          cancelRefetch: false
        });
      }
    }
  }, [fetchNextPage, isFetching, isTabActive, flatData.length, hasReachedTheEnd, hasFailure]);
  const fetchMoreOnTopReached = useCallback(() => {
    if (isTabActive && !hasFailure && scrollToTopRef !== null && scrollToTopRef.current !== null) {
      const element = scrollToTopRef.current;
      const rect = element.getBoundingClientRect();
      const scrollY = window.scrollY;

      // Calculate element's absolute position in document
      const elementTopInDocument = scrollY + rect.top;
      // Fetch if we're between [500px below the element] and [top of page]
      const shouldFetch = scrollY <= elementTopInDocument + 500;
      if (shouldFetch && !isFetching) {
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchPreviousPageTime.current;

        // If it's been more than 3 minutes, fetch from scratch
        if (timeSinceLastFetch > 3 * 60 * 1000) {
          clearFlashedRows();
          // Reset the query completely to initial state and refetch
          void queryClient.resetQueries({
            queryKey: ["quotes", params]
          });
          lastFetchPreviousPageTime.current = now;
          return;
        }

        // Otherwise, fetch previous page as normal
        if (flatData.length === 0) {
          queryClient.removeQueries({
            queryKey: ["quotes", params]
          });
        }
        void fetchPreviousPage({
          cancelRefetch: false
        });
        lastFetchPreviousPageTime.current = now;
      }
    }
  }, [fetchPreviousPage, isFetching, isTabActive, flatData.length, params, queryClient, scrollToTopRef, clearFlashedRows, hasFailure]);

  // continuously check if user is at the top of the page
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isTabActive) {
        fetchMoreOnTopReached();
      }
    }, TRANSACTIONS_POLLING_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchMoreOnTopReached, isTabActive]);

  // a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
  useEffect(() => {
    fetchMoreOnBottomReached();
  }, [fetchMoreOnBottomReached]);
  useEffect(() => {
    function handleScroll() {
      fetchMoreOnBottomReached();
    }
    document.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [fetchMoreOnBottomReached]);
  const table = useReactTable({
    data: flatData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    debugTable: true,
    defaultColumn: {
      size: 600,
      minSize: 0,
      maxSize: 600
    }
  });
  const {
    rows
  } = table.getRowModel();
  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => {
      return 93;
    },
    // estimate row height for accurate scrollbar dragging
    overscan: 50
  });

  // Restore scroll position after virtualizer is ready with all data
  useEffect(() => {
    if (!isFetching && rowVirtualizer.getTotalSize() > 0) {
      const rafId = requestAnimationFrame(() => {
        restoreScrollPosition();
      });
      return () => {
        cancelAnimationFrame(rafId);
      };
    }
    return undefined;
  }, [isFetching, rowVirtualizer, restoreScrollPosition]);
  useEffect(() => {
    return () => {
      // clean up timeouts for flashing rows
      // eslint-disable-next-line react-hooks/exhaustive-deps
      for (const timeout of Object.values(timeoutsForFlashingRows.current)) {
        clearTimeout(timeout);
      }
    };
  }, []);

  // Only reset query cache when params actually change (not on mount)
  const paramsStringified = JSON.stringify(params);
  const prevParamsStringified = usePrevious(paramsStringified);
  useEffect(() => {
    if (prevParamsStringified !== null && prevParamsStringified !== paramsStringified) {
      queryClient.removeQueries({
        queryKey: ["quotes", params]
      });
      clearFlashedRows();
      setHasReachedTheEnd(false);
    }
  }, [paramsStringified, prevParamsStringified, params, queryClient, clearFlashedRows]);
  const isFetchLoaderVisible = isFetching && isFetchingNextPage;
  useEffect(() => {
    if (!isFetching && isFetchingNextPage) {
      setIsFetchingNextPage(false);
    }
  }, [isFetching, isFetchingNextPage]);

  // Update visible deposit addresses whenever virtual items change
  useEffect(() => {
    const virtualItems = rowVirtualizer.getVirtualItems();
    const oneClickAddresses: DepositKey[] = [];
    const maskingAddresses: DepositKey[] = [];
    for (const virtualRow of virtualItems) {
      const row = rows[virtualRow.index];
      if (row === undefined) {
        throw new Error("Unreachable: Row is undefined");
      }

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unnecessary-type-assertion
      const tx = row.getValue("link") as LinkCell;
      if (!statusesToTrack.has(tx.status)) {
        continue;
      }
      const entry = {
        depositAddress: tx.depositAddress,
        depositMemo: tx.depositMemo ?? null
      };
      if (tx.source === "swaps") {
        oneClickAddresses.push(entry);
      } else {
        maskingAddresses.push(entry);
      }
    }
    setVisibleAddresses({
      swaps: oneClickAddresses.slice(0, MAX_NUMBER_OF_TRANSACTIONS),
      masking: maskingAddresses.slice(0, MAX_NUMBER_OF_TRANSACTIONS)
    });
  }, [rowVirtualizer, rows]);

  // Update visible quotes periodically based on cache TTL
  useEffect(() => {
    const updateVisibleQuotes = async () => {
      if (visibleAddresses.swaps.length === 0 && visibleAddresses.masking.length === 0 || !isTabActive) {
        return;
      }
      try {
        const [oneClickResults, maskingResults] = await Promise.all([visibleAddresses.swaps.length > 0 ? getTransactionsByIds(visibleAddresses.swaps) : [], visibleAddresses.masking.length > 0 ? getMaskingTransactionsByIds(visibleAddresses.masking) : []]);
        const updatedByKey = Object.fromEntries([...oneClickResults, ...maskingResults].map(tx => {
          return [getTransactionCacheKey(tx), tx] as const;
        }));

        // Update the infinite query data with fresh quotes
        queryClient.setQueryData<InfiniteData<TransactionsResponse>>(["quotes", params], prevData => {
          return prevData !== undefined ? {
            ...prevData,
            pages: prevData.pages.map(page => {
              return {
                ...page,
                items: page.items.map(tx => {
                  const updated = updatedByKey[getTransactionCacheKey(tx)];
                  if (updated === undefined) {
                    return tx;
                  }
                  return {
                    ...tx,
                    status: updated.status,
                    ...(updated.amountInFormatted !== undefined && {
                      amountInFormatted: updated.amountInFormatted
                    }),
                    ...(updated.amountOutFormatted !== undefined && {
                      amountOutFormatted: updated.amountOutFormatted
                    }),
                    // A row that settles while it is on screen gets
                    // new amounts, so its USD has to move with them.
                    // Only swap rows carry it.
                    ...(updated.source === "swaps" && {
                      amountInUsd: updated.amountInUsd,
                      amountOutUsd: updated.amountOutUsd
                    })
                  };
                })
              };
            })
          } : prevData;
        });
      } catch (error) {
        logger.error("[table] Failed to update visible transaction statuses", error);
      }
    };
    const intervalId = setInterval(() => {
      void updateVisibleQuotes();
    }, TRANSACTIONS_BY_IDS_POLLING_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [visibleAddresses, params, queryClient, isTabActive]);

  // With no rows there is nothing to be newer or older than, and nothing above
  // the table to attach to, so one undirected panel goes below it.
  const hasRows = flatData.length > 0;
  const failureAtTop = failure?.fetched === "newer" && hasRows;
  const failurePanel = failure === undefined ? null : <QueryFailed what={hasRows && failure.fetched !== "initial" ? `${failure.fetched} transactions` : "transactions"} correlationId={failure.correlationId} offerClearFilters={hasActiveFilters(params)} />;
  return <div className="flex min-h-dvh w-full flex-col gap-4">
			{failureAtTop && failurePanel}
			<Table className="mx-auto grid max-w-3xl xl:max-w-5xl">
				<TableCaption className="sr-only">Intents</TableCaption>
				<TableHeader className="bg-background sticky top-0 z-1 grid">
					{table.getHeaderGroups().map(headerGroup => {
          return <TableRow key={headerGroup.id} className="flex w-full justify-center border-b-0! hover:bg-transparent">
								{headerGroup.headers.map(header => {
              return <TableHead key={header.id} className="flex items-center p-0" style={{
                width: header.getSize()
              }}>
											<div className={`w-full ${header.id === "link" ? "" : "px-4"}`}>
												{flexRender(header.column.columnDef.header, header.getContext())}
											</div>
										</TableHead>;
            })}
							</TableRow>;
        })}
				</TableHeader>
				<TableBody className="relative" style={{
        height: `${numToStr(rowVirtualizer.getTotalSize())}px`,
        width: "100%",
        position: "relative"
      }}>
					{rowVirtualizer.getVirtualItems().map(virtualRow => {
          const row = rows[virtualRow.index];
          if (row === undefined) {
            throw new Error("Unreachable: Row is undefined");
          }
          return <TableRowWithHover key={row.id} row={row} virtualRow={virtualRow} flashedRows={flashedRows} addFlashedRow={addFlashedRow} timeoutsForFlashingRows={timeoutsForFlashingRows} />;
        })}
				</TableBody>
			</Table>
			{failure !== undefined && !failureAtTop && failurePanel}
			{!hasFailure && isNoResultsVisible && <div className="text-muted-foreground flex h-16 min-h-[500px] items-center justify-center text-center">
					No results. Try clearing some filters.
				</div>}
			{isFetchLoaderVisible && <output aria-live="polite" className="slide-in-from-bottom-4 animate-in bg-background/90 border-brand-orange/20 fixed right-4 bottom-6 left-4 z-50 mx-auto flex max-w-md items-center gap-3 rounded-xl border px-4 py-3 shadow-2xl backdrop-blur-md duration-300 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 sm:gap-4 sm:px-6 sm:py-4">
					<Loader size="default" className="shrink-0" />
					<div className="flex flex-col gap-1">
						<span className="text-brand-orange font-medium">
							Loading More Transactions
						</span>
						<span className="text-muted-foreground text-sm">
							Fetching the next page...
						</span>
					</div>
				</output>}
		</div>;
}

/** Cursors and the page size are not filters, so clearing them changes nothing. */
function hasActiveFilters({
  limit: _limit,
  nextCursor: _nextCursor,
  prevCursor: _prevCursor,
  ...filters
}: GetTransactionsParsedSearchParams): boolean {
  return Object.values(filters).some(value => {
    return value !== undefined;
  });
}
function loadFlashedRowsFromStorage(key: string): Set<string> | null {
  if (typeof window === "undefined") {
    return null;
  }
  const saved = sessionStorage.getItem(key);
  if (saved === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(saved);
    if (Array.isArray(parsed) && parsed.every(item => {
      return typeof item === "string";
    })) {
      return new Set<string>(parsed);
    }
  } catch {
    // If parsing fails, return null
  }
  return null;
}
function useFlashedRows(initialItems) {
  const $ = _c(10);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = readonlyURLSearchParamsToStr(searchParams);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const flashedRowsKey = `flashed-rows-${pathname}-${t0}`;
  const flashedRowsRef = useRef(null);
  if (flashedRowsRef.current === null) {
    let t1;
    if ($[2] !== flashedRowsKey || $[3] !== initialItems) {
      t1 = loadFlashedRowsFromStorage(flashedRowsKey) ?? new Set(initialItems.map(_temp));
      $[2] = flashedRowsKey;
      $[3] = initialItems;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    flashedRowsRef.current = t1;
  }
  let t1;
  if ($[5] !== flashedRowsKey) {
    t1 = id => {
      flashedRowsRef.current?.add(id);
      if (typeof window !== "undefined" && flashedRowsRef.current !== null) {
        const flashedArray = Array.from(flashedRowsRef.current);
        sessionStorage.setItem(flashedRowsKey, JSON.stringify(flashedArray));
      }
    };
    $[5] = flashedRowsKey;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const addFlashedRow = t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      flashedRowsRef.current?.clear();
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const clearFlashedRows = t2;
  let t3;
  if ($[8] !== addFlashedRow) {
    t3 = {
      flashedRows: flashedRowsRef,
      addFlashedRow,
      clearFlashedRows
    };
    $[8] = addFlashedRow;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function _temp(tx) {
  return getTransactionCacheKey(tx);
}